import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookmark as faBookmarkSolid } from "@fortawesome/free-solid-svg-icons";
import { toggleMentorBookmark } from "../../stores/bookmarks/mentorBookmarksSlice";
import styles from "./CardStyles.module.css";
import cx from "classnames";
import type { Mentor } from "../../shared/mentorTypes";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { bookmarkEventBuilder, sendEvent } from "../../utils/event-helpers";
import { useNavigate } from "react-router-dom";
import useTenant from "../../tenancy/useTenantPath";

interface CardProps {
  mentor: Mentor;
}

const BookmarkMentorsCard = ({ mentor }: CardProps) => {
  const { _id, name, title, thumbnail } = mentor;
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const tenant = useTenant();

  const { userId, sessionId } = useAppSelector((state) => state.session);
  const bookmarkedMentors = useAppSelector(
    (state) => state.mentorBookmarks.bookmarks,
  );

  const isBookmarked = bookmarkedMentors.some(
    (mentor) => mentor.mentorId === _id,
  );

  const handleBookmark = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!name) {
      console.warn("⚠️ Bookmark skipped due to missing bookmark ID.");
      return;
    }

    if (!userId || !sessionId) {
      console.warn("⚠️ Bookmark skipped due to missing user or session ID.");
      return;
    }

    const eventData = bookmarkEventBuilder(
      !isBookmarked,
      userId,
      sessionId,
      "Mentor",
      name,
      {},
    );
    void dispatch(sendEvent(eventData));

    dispatch(
      toggleMentorBookmark({
        userId,
        mentorId: _id,
      }),
    );
  };

  const handleClick = () => {
    navigate(tenant.basePath + "/mentorpal?mentorId=" + mentor._id);
  };

  return (
    <div
      className={styles.bookmarkCard}
      data-cy="bookmarked-mentor-card"
      data-id={mentor._id}
      onClick={handleClick}
    >
      <div className={styles.bookmarkCardContent}>
        <img src={thumbnail} alt={title} className={styles.bookmarkCardImg} />
        <div className={styles.bookmarkCardText}>
          <p className={styles.cardTitle}>{name}</p>
          <h3 className={styles.cardSubtitle}>{title}</h3>
        </div>
      </div>

      <button
        data-cy="bookmark-card"
        onClick={handleBookmark}
        className={styles.bookmarkCardBookmarkButton}
        aria-label={isBookmarked ? "Remove Bookmark" : "Add Bookmark"}
      >
        <FontAwesomeIcon
          icon={isBookmarked ? faBookmarkSolid : faBookmarkSolid}
          className={cx(
            styles.cardBookmarkIcon,
            isBookmarked ? styles.blue : styles.white,
          )}
        />
      </button>
    </div>
  );
};

export default BookmarkMentorsCard;
